import Router from 'next/router'
import PropTypes from 'prop-types'
import React from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import categoryDuck from 'components/category/category-item/duck'
import filterDuck from 'components/filter/filter-options/duck'
import pomeloFilterDuck from 'components/filter/pomelo-filter/duck'
import CommonLayout from 'components/layout/common'
import FallBackModal, { FALLBACK_TYPE } from 'components/portal/fall-back-modal'
import productListDuck from 'components/product/product-list/duck'
import CategoryLayout from 'components/shared/category/layout'
import sortDuck from 'components/sort/sort-options/duck'
import visibilityDuck, {
  PRODUCT_LIST_LAYOUT_VIEW,
} from 'components/visibility/duck'
import { DISABLE_NAV_PROGRESS } from 'constants/cookies'
import { ERROR_PAGE_TYPE } from 'constants/error'
import CategoryApiHandler from 'lib/api/category'
import ProductsApiHandler from 'lib/api/products'
import loadCategoryContent from 'lib/api/category-content'
import { withI18next } from 'lib/i18n/withI18next'
import { segmentTrackProductListViewed } from 'lib/segment'
import {
  SEGMENT_CATEGORY_QUERY_ID,
  SEGMENT_STORAGE_KEY_CATEGORY,
  SEGMENT_STORAGE_KEY_PRODUCT,
} from 'lib/segment/const'
import StateStorage from 'lib/state-storage'
import { appendObjToSessionObj } from 'lib/utils/common/commonUtils'
import { isObject } from 'lib/utils/common/object'
import {
  getProductIDs,
  isAuthCookiesAvailable,
} from 'lib/utils/handle-response'
import Error from 'pages/_error'
import { PAGE_NAME } from 'pages/const'
import ProductListSkeleton from 'components/product/product-list-skeleton'
import BasePage from '../base'
import { PRODUCTS_PER_PAGE, STORAGE_KEY_LAST_START_PAGE } from './const'
import CategoryProductList from './list'
import isCategoryGridView from './category-grid'
import createWorkwearPagination, {
  carryWorkwearPaginationOffset,
  getWorkwearPaginationKey,
} from './workwear-pagination'
import LoadMoreStatus from './load-more-status'
import {
  canRestoreWorkwearReturn,
  clearWorkwearReturn,
  rememberWorkwearReturn,
  restoreWorkwearScroll,
  takeWorkwearReturn,
} from './workwear-return'
import styles from './style.scss'
import {
  checkAndGetCacheResponse,
  checkForSendSegmentTrackProductListViewed,
  getAndSaveFiltersFromQueryParams,
  getValidSortParam,
  loadMoreProductsUtil,
  parseProductPageNo,
  syncRoutePath,
  trackCategoryPage,
  getBreadcrumbSchema,
  isNewCategoryFiltersAndSort,
  getSortFilterOptions,
} from './util'

class Category extends BasePage {
  static stackIndex = 0

  static shouldRunGetInitialProps = true

  static shouldUpdateStartPageNo = true

  static startPageNo = 1

  static async getInitialProps({
    asPath,
    device,
    isServer,
    pathname,
    query,
    req,
    res,
    segment_anonymous_id,
    store,
    user_uid,
  }) {
    const startPageNo = parseProductPageNo(query.page)

    if (!isServer && !Category.shouldRunGetInitialProps) {
      Category.shouldRunGetInitialProps = true

      if (Category.shouldUpdateStartPageNo) {
        Category.startPageNo = startPageNo
      } else {
        Category.shouldUpdateStartPageNo = true
      }

      return {}
    }

    Category.startPageNo = startPageNo

    if (isServer) {
      BasePage.loadInitialContentFromServer({
        pathname,
        query,
        req,
        res,
        store,
        skipAuthorization: true,
      })
    } else {
      BasePage.loadInitialContentFromClient(req, res, store)
    }

    const { id } = query
    const zone = query.zone || StateStorage.getShop()

    if ((id === '1214' || id === 'beet' || id === 'beauty') && zone === 'id') {
      return { error: ERROR_PAGE_TYPE.beetNotAvailable }
    }

    try {
      return await Category.getInitialContent({
        asPath,
        device,
        isServer,
        query,
        req,
        res,
        segment_anonymous_id,
        store,
        user_uid,
      })
    } catch (e) {
      // Due to how fallbackModal is working, this is to make it properly hide/show
      // In the case that the history stack is in this format /category1 => /category2 => /category3
      Category.stackIndex += 1

      // TODO: Auto re-fetch on client side if SSR timed out.
      // if (e.code === ERROR_CODES.timeout) {
      //   return {}
      // }

      return {
        error: e?.message || e?.body?.error?.message,
        key: Category.stackIndex,
      }
    }
  }

  // TODO: Change the code to behave in one data direction flow,
  // controlling all with getInitialProps. (to support back/forward navigation)
  static async getInitialContent({
    asPath,
    device,
    isServer,
    query,
    req,
    res,
    segment_anonymous_id,
    store,
    user_uid,
  }) {
    const workwearReturn =
      !isServer &&
      takeWorkwearReturn({
        asPath,
        fromPath: Router.asPath,
        state: store.getState(),
      })
    if (workwearReturn) {
      Category.startPageNo = workwearReturn.startPageNo
      appendObjToSessionObj(SEGMENT_STORAGE_KEY_PRODUCT.stack, {
        [SEGMENT_STORAGE_KEY_PRODUCT.category_uuid]: true,
      })
      return { workwearReturn }
    }

    const {
      brand: brandQuery,
      category: categoriesQuery,
      color: colorQuery,
      filter_size: sizeQuery,
      id,
      price: priceQuery,
      subcategory: categoryQuery,
      sustainability,
      click_position,
      ref_id,
      ref_type,
      sub_postion,
    } = query

    const categoryId = id || 2
    const sortQuery = getValidSortParam(query?.sort)
    const {
      category: reduxCategory,
      filter: reduxFilter,
      products: categoryProducts,
      sort: reduxSort,
    } = store.getState()
    let isSameCategoryAsRedux = false

    const sizeFromQuery = sizeQuery?.split(',').sort() || []
    const sizeFromRedux =
      reduxFilter?.find((filter) => filter.id === 'size')?.values.sort() || []

    const isSameSizeFilter =
      JSON.stringify(sizeFromQuery) === JSON.stringify(sizeFromRedux)

    // Check for logic to use cached response.
    if (
      !isServer &&
      categoryProducts.payload?.products?.length > 0 &&
      isSameSizeFilter &&
      isObject(reduxCategory.payload)
    ) {
      const cacheResponse = checkAndGetCacheResponse({
        asPath,
        categoryId,
        reduxCategory,
        reduxSort,
        setIsSameCategoryAsRedux: (val) => {
          isSameCategoryAsRedux = val
        },
        sortQuery,
        store,
      })

      if (cacheResponse) {
        Category.startPageNo = parseProductPageNo(
          sessionStorage.getItem(STORAGE_KEY_LAST_START_PAGE),
        )

        return {}
      }
    }

    const shop = req?.params?.zone || query.zone
    const language = req?.params?.lang || query.lang
    const sort = sortQuery ? { by: sortQuery } : null

    if (!isSameCategoryAsRedux) {
      store.dispatch(filterDuck.creators.clean())
      store.dispatch(visibilityDuck.creators.clean())
      store.dispatch(pomeloFilterDuck.creators.reset())
    }

    store.dispatch(productListDuck.creators.invalidate())

    const filterData = getAndSaveFiltersFromQueryParams({
      brandQuery,
      categoriesQuery,
      categoryQuery,
      colorQuery,
      priceQuery,
      sustainability,
      reduxFilter,
      sizeQuery,
      store,
      asPath,
    })

    // Once the slug is resolved, metadata and products can load together.
    const auth = StateStorage.getCookie('auth')
    const isGuestMode = !isAuthCookiesAvailable(auth)
    const categoryOptions = {
      categoryId,
      shop,
      language,
      isGuestMode,
      user_uid,
      segment_anonymous_id,
      device,
      ...(Boolean(filterData) && { filter: filterData }),
    }

    const productOptions = {
      isGuestMode,
      language,
      limit: PRODUCTS_PER_PAGE,
      shop,
      skip: (Category.startPageNo - 1) * PRODUCTS_PER_PAGE,
      sort,
      ...(Boolean(filterData) && { filter: filterData }),
      ...(Boolean(user_uid) && { userUId: user_uid }),
      ...(Boolean(segment_anonymous_id) && {
        segmentAnonymousID: segment_anonymous_id,
      }),
      ...(Boolean(device) && { device }),
    }

    const { category: categoryRes, products: productsRes } =
      await loadCategoryContent(categoryOptions, productOptions)

    sortDuck.creators.clean()
    store.dispatch(sortDuck.creators.update(productsRes.default_sort))
    store.dispatch(categoryDuck.creators.receive(categoryRes))
    store.dispatch(productListDuck.creators.receive(productsRes))

    if (!isServer) {
      // Redirect from hero banner cause losing track data
      if (ref_type) {
        sessionStorage.setItem(SEGMENT_STORAGE_KEY_CATEGORY.id, ref_id)
        sessionStorage.setItem(
          SEGMENT_STORAGE_KEY_CATEGORY.position,
          click_position,
        )
        sessionStorage.setItem(SEGMENT_STORAGE_KEY_CATEGORY.type, ref_type)
        sessionStorage.setItem(SEGMENT_STORAGE_KEY_CATEGORY.subPos, sub_postion)
      }
      // NOTE: Google Tag Manager has its own update from the current route/path and without
      // setImmediate the function will trigger too fast, before the route/path is changed.
      setImmediate(() => {
        trackCategoryPage({
          category: categoryRes,
          categoryProducts: productsRes,
          filter: reduxFilter,
          pageNo: Category.startPageNo,
          sort: { by: productsRes.default_sort },
        })
      })
    } else if (productsRes?.products?.length === 0) {
      res.status(404)
    }

    return {
      isPageSSRLoaded: isServer,
      segment_anonymous_id,
      startPageNo: Category.startPageNo,
      user_uid,
    }
  }

  constructor(props) {
    super(props, false)

    this.state = {
      apiResError: false,
      isFetchingProducts: false,
      workwearLoadMoreStatus: 'idle',
      relatedCategories: null,
    }

    // Value for static vars don't transfer from SSR to CSR, need to sync it here.
    if (props.isPageSSRLoaded) {
      Category.startPageNo = props.startPageNo
    }
  }

  afterPageMount() {
    const {
      category,
      categoryProducts,
      filter,
      internationalization,
      isPageSSRLoaded,
      sort,
    } = this.props
    const categoryId = category?.id_category || 0
    const isWorkwearReturn =
      this.getWorkwearGrid() &&
      canRestoreWorkwearReturn(this.props.workwearReturn, this.props)
    Router.events.on('routeChangeStart', this.saveWorkwearReturn)
    Router.events.on('routeChangeError', clearWorkwearReturn)
    document.body.classList.add('pomelo-page-category')
    document.body.setAttribute('rel', categoryId)

    // from search page
    const ref_type = sessionStorage.getItem(SEGMENT_STORAGE_KEY_CATEGORY.type)

    // id_category is the required field, this is short term solution before fix 403 on SSR
    if (categoryId && !isWorkwearReturn) {
      // Fix issue of can't identify user at SSR
      this.props.fetchCategory({
        categoryId,
        filter,
      })

      this.props.invalidateProducts()
      this.props.fetchCategoryProducts({
        categoryId,
        currencyId: internationalization.currency,
        filter: ref_type === 'search' ? [] : filter,
        limit: PRODUCTS_PER_PAGE,
        segmentAnonymousID: StateStorage.getSegmentAnonymousId(),
        shopId: internationalization.shop,
        skip: (Category.startPageNo - 1) * PRODUCTS_PER_PAGE,
        sort,
      })
      // Only track in afterPageMount if user comes from SSR.
      if (isPageSSRLoaded) {
        trackCategoryPage({
          category,
          categoryProducts,
          filter,
          internationalization,
          pageNo: Category.startPageNo,
          sort,
        })
      }
    }

    this.getRelatedCategories()
    sessionStorage.removeItem(STORAGE_KEY_LAST_START_PAGE)
    if (isWorkwearReturn) {
      this.cancelWorkwearScroll = restoreWorkwearScroll(
        this.props.workwearReturn,
      )
    }
  }

  componentDidUpdate(prevProps) {
    if (this.workwearPagination) this.workwearPagination.sync()
    const { category, updateFilters } = this.props
    if (
      this.cancelWorkwearScroll &&
      !canRestoreWorkwearReturn(this.props.workwearReturn, this.props)
    ) {
      this.cancelWorkwearScroll()
    }
    // Changing between categories
    if (prevProps.category?.id_category !== category?.id_category) {
      this.resetRelatedCategories()
      this.getRelatedCategories()
      document.body.setAttribute('rel', `${category?.id_category || 0}`)
    }

    if (isNewCategoryFiltersAndSort(category?.filters)) {
      updateFilters(category?.filters)
    }
  }

  onPageLeave() {
    if (this.workwearPagination) this.workwearPagination.dispose()
    Router.events.off('routeChangeStart', this.saveWorkwearReturn)
    Router.events.off('routeChangeError', clearWorkwearReturn)
    if (this.cancelWorkwearScroll) this.cancelWorkwearScroll()
    sessionStorage.setItem(STORAGE_KEY_LAST_START_PAGE, Category.startPageNo)
    appendObjToSessionObj(SEGMENT_STORAGE_KEY_PRODUCT.stack, {
      [SEGMENT_STORAGE_KEY_PRODUCT.category_uuid]: null,
    })

    document.body.classList.remove('pomelo-page-category')
    document.body.removeAttribute('rel')
  }

  resetRelatedCategories = () => this.setState({ relatedCategories: null })

  saveWorkwearReturn = (destination) => {
    if (this.cancelWorkwearScroll) this.cancelWorkwearScroll()
    rememberWorkwearReturn({
      enabled:
        this.getWorkwearGrid() &&
        !this.state.isFetchingProducts &&
        !this.state.apiResError,
      path: Router.asPath,
      destination,
      props: this.props,
      startPageNo: Category.startPageNo,
    })
  }

  async getRelatedCategories() {
    const { category } = this.props

    // We disable related categories for All Clothes
    if (category && category.id_category === 2) {
      this.setState({ relatedCategories: { children: [] } })
      return
    }

    try {
      const res = await CategoryApiHandler.getCategoryRelated(
        category.id_category,
      )

      if (res) {
        this.setState({ relatedCategories: res })
      }
    } catch (e) {
      this.setState({ relatedCategories: {} })
    }
  }

  // This function loads 2nd page of products and up.
  loadMoreProducts = () => {
    if (this.getWorkwearGrid()) return this.loadMoreWorkwearProducts()
    const {
      addCategoryProducts,
      category,
      categoryProducts,
      filter,
      internationalization,
      user_uid,
      segment_anonymous_id,
      sort,
    } = this.props

    return loadMoreProductsUtil({
      addCategoryProducts,
      category,
      categoryProducts,
      filter,
      internationalization,
      user_uid,
      segment_anonymous_id,
      sort,
      startPageNo: Category.startPageNo,
      setIsFetchingProducts: (bool) =>
        this.setState({ isFetchingProducts: bool }),
      setApiResError: (bool) => this.setState({ apiResError: bool }),
    })
  }

  loadMoreWorkwearProducts = (retry = false) => {
    if (!this.workwearPagination) {
      this.workwearPagination = createWorkwearPagination({
        getContext: () => ({
          key: getWorkwearPaginationKey(this.props, Category.startPageNo),
          payload: this.props.categoryProducts,
          enabled:
            this.getWorkwearGrid() &&
            !this.props.categoryProducts?.sub_categories?.length,
          fetching: this.props.productsFetching,
          startPageNo: Category.startPageNo,
          props: this.props,
        }),
        fetchPage: ({ props }, skip) =>
          ProductsApiHandler.getList({
            categoryId: props.category.id_category,
            currencyId: props.internationalization.currency,
            filter: props.filter,
            isGuestMode: StateStorage.isGuestMode(),
            limit: PRODUCTS_PER_PAGE,
            segmentAnonymousID: props.segment_anonymous_id,
            shopId: props.internationalization.shop,
            skip,
            sort: props.sort,
            userUId: props.user_uid,
          }),
        append: (payload) => this.props.updateCategoryProducts(payload),
        onStatus: (status) =>
          this.setState({
            workwearLoadMoreStatus: status,
            isFetchingProducts: status === 'loading' || status === 'retrying',
          }),
        onLoaded: ({ props }, skip, products) =>
          checkForSendSegmentTrackProductListViewed(
            { payload: { products } },
            {
              categoryId: props.category.id_category,
              category: props.category,
              filter: props.filter,
              sort: props.sort,
              pageToSkip: Math.floor(skip / PRODUCTS_PER_PAGE),
            },
          ),
      })
    }
    return this.workwearPagination.load(retry)
  }

  retryWorkwearProducts = () => this.loadMoreWorkwearProducts(true)

  loadPreviousProductPage = async (onFinish) => {
    const {
      category: { id_category: categoryId, name: categoryName },
      updateCategoryProducts,
      filter,
      internationalization: { currency, shop: shopId } = {},
      user_uid: userUId,
      segment_anonymous_id: segmentAnonymousID,
      sort,
    } = this.props

    const pageToSkip = Category.startPageNo - 2

    try {
      const res = await ProductsApiHandler.getList({
        categoryId,
        currencyId: currency,
        filter,
        isGuestMode: StateStorage.isGuestMode(),
        limit: PRODUCTS_PER_PAGE,
        segmentAnonymousID,
        shopId,
        skip: pageToSkip * PRODUCTS_PER_PAGE,
        sort,
        userUId,
      })

      if (res?.error) {
        this.setState({ apiResError: true })
      } else if (res?.products) {
        const { categoryProducts } = this.props
        const ref_type = sessionStorage.getItem(
          SEGMENT_STORAGE_KEY_CATEGORY.type,
        )

        Category.startPageNo -= 1

        updateCategoryProducts(
          carryWorkwearPaginationOffset(categoryProducts, {
            ...categoryProducts,
            products: [...res.products, ...categoryProducts.products],
          }),
        )

        sessionStorage.setItem(SEGMENT_CATEGORY_QUERY_ID, res.id_recommendation)
        segmentTrackProductListViewed(
          {
            active_filters: filter,
            id_site_category: categoryId,
            site_category_name: categoryName || '',
            page_number: pageToSkip + 1,
            product_list_ids: getProductIDs(res.products),
            sort_by: sort.by,
          },
          {
            ref_type,
          },
        )
      }

      onFinish()
    } catch (e) {
      onFinish()
    }
  }

  getPageImage() {
    const { category, categoryProducts } = this.props

    let image

    if (category.banner?.web_mobile) {
      image = category.banner.web_mobile
    } else if (categoryProducts?.products?.length > 0) {
      const { full_cover_img_v2: coverImg } = categoryProducts.products[0]

      if (coverImg) {
        image = coverImg
      }
    }

    return image
  }

  getPageMeta() {
    const { category, t } = this.props

    const meta = { image: this.getPageImage() }

    if (category.meta) {
      meta.title = category.meta.title || meta.title
      meta.description = category.meta.description || meta.description
    } else {
      meta.title = `${category.name || t('Clothes')} - Pomelo Fashion`
      meta.description = t(
        'Shop the latest fashion at POMELO fashion. Designed and manufactured in house for quality and style. Dresses, tops, knits, jackets, denim, accessories and more!',
      )
    }

    return meta
  }

  invokeSyncRoutePath(viewportPageNo) {
    const { category, filter, sort } = this.props

    syncRoutePath({
      categoryId: category.id_category,
      categoryRewrite: category.link_rewrite,
      filterData: filter,
      setShouldRunGetInitialProps: (val) => {
        Category.shouldRunGetInitialProps = val
      },
      sortData: sort.by,
      viewportPageNo,
    })
  }

  onProductPageChanged = (additionalPage = 0) => {
    // Handle the switching back and forward between category and non-category page issue.
    if (window.location.pathname !== Router.asPath.split('?')[0]) {
      return
    }

    sessionStorage.setItem(DISABLE_NAV_PROGRESS, '1')
    Category.shouldUpdateStartPageNo = false

    this.invokeSyncRoutePath(Category.startPageNo + additionalPage)
  }

  // For mobile filter apply / reset calls the endpoints inside the components not here.
  setPageOne = async (resetPageDesktop) => {
    const {
      category,
      internationalization,
      invalidateProducts,
      fetchCategory,
      fetchCategoryProducts,
      sort,
    } = this.props
    this.invokeSyncRoutePath(1)

    if (!resetPageDesktop) {
      return
    }

    const categoryId = category.id_category
    const segmentAnonymousID = StateStorage.getSegmentAnonymousId()

    invalidateProducts()

    const res = await Promise.all([
      fetchCategoryProducts({
        categoryId,
        currencyId: internationalization.currency,
        filter: [],
        limit: PRODUCTS_PER_PAGE,
        segmentAnonymousID,
        shopId: internationalization.shop,
        skip: 0,
        sort,
      }),
      fetchCategory({ categoryId, filter: [] }),
    ])

    if (res?.length) {
      if (res[0]?.error || res[1]?.error) {
        this.setState({ apiResError: true })
        // Only track if filter result is valid.
      } else if (res[0]?.products?.length > 0) {
        const productIds = getProductIDs(res[0].products)
        const ref_type = sessionStorage.getItem(
          SEGMENT_STORAGE_KEY_CATEGORY.type,
        )

        /* Ignore this test for now to reduce conflict with WEBS-2582, will do this test later */
        /* istanbul ignore next */
        segmentTrackProductListViewed(
          {
            id_site_category: categoryId,
            page_number: 1,
            product_list_ids: productIds,
            site_category_name: category.name || '',
            sort_by: sort.by,
          },
          {
            ref_type,
          },
        )
      }
    }
  }

  getWorkwearGrid() {
    return isCategoryGridView(this.props)
  }

  renderSubCategory = (sub, idx) => {
    const { category, isPhone, view } = this.props

    return (
      <CategoryProductList
        hideVirtualTryOn
        category={category}
        displayQuickAddToBag={
          view === PRODUCT_LIST_LAYOUT_VIEW.BLOCK_VIEW ?? false
        }
        displayHoverInteractions
        idx={idx}
        isPhone={isPhone}
        isSubCategory
        workwearGrid={this.getWorkwearGrid()}
        key={sub.id_category}
        list={sub.products}
        refId={sub.id_category}
        showTeaser
        title={sub.name}
      />
    )
  }

  renderProducts() {
    const {
      category,
      categoryProducts: { products, sub_categories } = {},
      isPhone,
      view,
    } = this.props
    const { isFetchingProducts, workwearLoadMoreStatus } = this.state
    const workwearGrid = this.getWorkwearGrid()

    if (sub_categories?.length) {
      return sub_categories.map(this.renderSubCategory)
    }

    if (products) {
      return (
        <InfiniteScroll
          key={
            workwearGrid
              ? getWorkwearPaginationKey(this.props, Category.startPageNo)
              : undefined
          }
          next={this.loadMoreProducts}
          dataLength={products.length}
          scrollThreshold={0.7}
          hasChildren={products.length > 0}
          hasMore={
            !isFetchingProducts &&
            (!workwearGrid || workwearLoadMoreStatus === 'idle')
          }
        >
          <CategoryProductList
            hideVirtualTryOn
            category={category}
            displayQuickAddToBag={
              view === PRODUCT_LIST_LAYOUT_VIEW.BLOCK_VIEW ?? false
            }
            displayHoverInteractions
            isPhone={isPhone}
            list={products}
            pageName="category"
            workwearGrid={this.getWorkwearGrid()}
            refId={category.id_category}
            showTeaser
            onProductPageChanged={this.onProductPageChanged}
          />
          {workwearGrid && (
            <LoadMoreStatus
              status={workwearLoadMoreStatus}
              onRetry={this.retryWorkwearProducts}
              t={this.props.t}
              isThai={this.props.internationalization.language === 'th'}
            />
          )}
        </InfiniteScroll>
      )
    }

    return null
  }

  renderHiddenPreviousNextLink() {
    const { originPath } = this.props
    const [parsedPath] = originPath.split('?')

    return (
      <div className="pagination__wrapper hidden">
        {Category.startPageNo > 1 && (
          <a href={`${parsedPath}?page=${Category.startPageNo - 1}`}>
            Previous page
          </a>
        )}
        <a href={`${parsedPath}?page=${Category.startPageNo + 1}`}>Next page</a>
      </div>
    )
  }

  render() {
    const { category, categoryProducts, error, isPhone, t } = this.props
    const { relatedCategories, apiResError } = this.state

    if (error || !category || apiResError) {
      return (
        <React.Fragment>
          <Error error={error} />
          {error !== ERROR_PAGE_TYPE.beetNotAvailable && error !== '404' && (
            <FallBackModal
              keyIndex={Category.stackIndex}
              modalType={FALLBACK_TYPE.close}
            />
          )}
        </React.Fragment>
      )
    }

    const {
      banner,
      brand_details,
      filters,
      id_category,
      name,
      total_products,
    } = category
    const breadcrumbSchema = getBreadcrumbSchema(category)
    const sortFilterOptions = getSortFilterOptions(category, filters)

    return (
      <CommonLayout
        isStickyHeader={!isPhone}
        meta={this.getPageMeta()}
        pageId={id_category || 0}
        pageName={PAGE_NAME.category}
        schema={breadcrumbSchema}
        showAdsBanner
      >
        <style jsx>{styles}</style>
        {this.renderHiddenPreviousNextLink()}
        <CategoryLayout
          workwearGrid={this.getWorkwearGrid()}
          categoryBanner={banner}
          categoryId={id_category}
          isPhone={isPhone}
          isNewCategoryFiltersAndSort={isNewCategoryFiltersAndSort(
            category?.filters,
          )}
          relatedCategories={relatedCategories?.children || null}
          setPageOne={this.setPageOne}
          sortFilterOptions={sortFilterOptions}
          t={t}
          title={name}
          totalProducts={total_products}
          brandDetails={brand_details}
          refId={category.id_category}
          {...(Category.startPageNo > 1 &&
            category &&
            categoryProducts && {
              onLoadPreviousClicked: this.loadPreviousProductPage,
            })}
        >
          {categoryProducts ? (
            this.renderProducts()
          ) : (
            <ProductListSkeleton
              number={18}
              workwearGrid={this.getWorkwearGrid()}
            />
          )}
        </CategoryLayout>
      </CommonLayout>
    )
  }
}

Category.defaultProps = {
  category: null,
  categoryProducts: null,
  customerId: undefined,
  productsFetching: false,
  isPageSSRLoaded: false,
  startPageNo: null,
  view: null,
  workwearReturn: null,
}

Category.propTypes = {
  addCategoryProducts: PropTypes.func.isRequired,
  category: PropTypes.shape({}),
  categoryProducts: PropTypes.shape({}),
  customerId: PropTypes.number,
  productsFetching: PropTypes.bool,
  workwearReturn: PropTypes.shape({}),
  fetchCategory: PropTypes.func.isRequired,
  fetchCategoryProducts: PropTypes.func.isRequired,
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  internationalization: PropTypes.shape({}).isRequired,
  invalidateProducts: PropTypes.func.isRequired,
  isPageSSRLoaded: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  originPath: PropTypes.string.isRequired,
  sort: PropTypes.shape({}).isRequired,
  startPageNo: PropTypes.number,
  t: PropTypes.func.isRequired,
  updateCategoryProducts: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  view: PropTypes.string,
}

export { Category }

const Extended = withI18next()(Category)

export default connect(
  (state) => ({
    category: state.category.payload,
    categoryProducts: state.products.payload,
    productsFetching: state.products.isFetching,
    customerId: state.auth?.user?.id_customer,
    filter: state.filter,
    internationalization: state.internationalization,
    isPhone: state.device.isPhone,
    originPath: state.device.originPath,
    sort: state.sort,
    view: state.visibility.view,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        updateFilters: filterDuck.creators.updateFilters,
        addCategoryProducts: productListDuck.creators.add,
        updateCategoryProducts: productListDuck.creators.receive,
        fetchCategory: categoryDuck.creators.fetch,
        fetchCategoryProducts: productListDuck.creators.get,
        invalidateProducts: productListDuck.creators.invalidate,
      },
      dispatch,
    ),
)(Extended)
